import { useMutation } from "@tanstack/react-query";

import { queryClient } from "@config/queryClient";
import useMeetingStore from "@store/useMeetingStore";
import usePlatformStore from "@store/usePlatformStore";
import meetingService from "@services/meeting.service";
import { IMeetingResponse } from "@interfaces/meeting.interface";
import { UpdateMeetingAttendeesRequest } from "@app-types/requests.type";
import { ADD_MEETING_ATTENDEE_MUTATION_KEY, MEETING_QUERY_KEY } from "@config/query";

export function useRemoveMeetingAttendee() {
  const { itemId } = usePlatformStore();
  const { meetingId } = useMeetingStore();

  return useMutation({
    mutationKey: [ADD_MEETING_ATTENDEE_MUTATION_KEY, meetingId],
    mutationFn: async (data: UpdateMeetingAttendeesRequest) => {
      return meetingService.removeMeetingAttendee(meetingId, data.payload);
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: [MEETING_QUERY_KEY, itemId] });

      // Snapshot the previous value
      const previousMeetingData = queryClient.getQueryData<IMeetingResponse>([
        MEETING_QUERY_KEY,
        itemId,
      ]);

      // Optimistically update to the new value
      queryClient.setQueryData([MEETING_QUERY_KEY, itemId], (old: IMeetingResponse) => {
        if (!old) return old;

        const attendees = variables?.attendees || old.meeting?.attendees || [];
        const people = variables?.people || old.meeting?.people || [];

        return {
          ...old,
          meeting: { ...old.meeting, attendees, people },
        };
      });

      // Return a context object with the snapshotted value
      return { previousMeetingData };
    },
    onError: (err, newTodo, context) => {
      if (context?.previousMeetingData) {
        // If the mutation fails, use the context returned from onMutate to roll back
        queryClient.setQueryData([MEETING_QUERY_KEY, itemId], context.previousMeetingData);
      }
    },
  });
}
